import { useCallback, useEffect, useSyncExternalStore } from "react";

export type Theme = "light" | "dark" | "system";

const STORAGE_KEY = "nn-theme";

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // localStorage may throw in private browsing or restricted contexts
  }
  return "system";
}

function resolveTheme(theme: Theme): "light" | "dark" {
  return theme === "system" ? getSystemTheme() : theme;
}

function applyTheme(theme: Theme): void {
  const resolved = resolveTheme(theme);
  const root = document.documentElement;

  if (resolved === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }

  root.style.colorScheme = resolved;
}

// Simple external store so multiple components can subscribe.
// The snapshot includes both the stored preference and the resolved value
// so that useSyncExternalStore detects changes when the OS theme changes
// while the user has selected "system" mode.
type ThemeSnapshot = { theme: Theme; resolved: "light" | "dark" };

let currentTheme: Theme = getStoredTheme();
let currentSnapshot: ThemeSnapshot = {
  theme: currentTheme,
  resolved: resolveTheme(currentTheme),
};
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): ThemeSnapshot {
  return currentSnapshot;
}

function setThemeInternal(theme: Theme): void {
  currentTheme = theme;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // localStorage may throw in private browsing or restricted contexts
  }
  applyTheme(theme);
  currentSnapshot = { theme, resolved: resolveTheme(theme) };
  listeners.forEach((l) => l());
}

// Initialize on module load
if (typeof window !== "undefined") {
  applyTheme(currentTheme);

  // Listen for system theme changes when mode is "system"
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", () => {
      if (currentTheme === "system") {
        applyTheme("system");
        // Update the snapshot so useSyncExternalStore detects the change
        currentSnapshot = { theme: "system", resolved: resolveTheme("system") };
        listeners.forEach((l) => l());
      }
    });
}

const serverSnapshot: ThemeSnapshot = { theme: "system", resolved: "dark" };

export function useTheme() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, () => serverSnapshot);

  const setTheme = useCallback((t: Theme) => {
    setThemeInternal(t);
  }, []);

  const toggleTheme = useCallback(() => {
    const resolved = resolveTheme(currentTheme);
    setThemeInternal(resolved === "dark" ? "light" : "dark");
  }, []);

  // Re-apply on mount to ensure DOM is in sync
  useEffect(() => {
    applyTheme(snapshot.theme);
  }, [snapshot.theme]);

  return {
    theme: snapshot.theme,
    resolvedTheme: snapshot.resolved,
    setTheme,
    toggleTheme,
  } as const;
}
